
import { ModelBase } from "@models/UpsertableBase";
import { FeatureUserRating } from "@models";
import { 
  AbilityEffect, 
  Potence, 
  // ItemLimitation, 
  SpellList, 
  TemplateBase,
} from "@dndModels";
import { AreaType, AttackType, CastingTime, DamageType, DataType, DurationType, EffectType, KnowledgeType, SchoolOfMagic, SpellLevel, TargetType } from "@dndModels/Types";
import { CASTING_TIME_MAP, STANDARD_CASTING_TIMES } from "@dndModels/Constants";

import CommonUtilities from "@utilities/common_utilities";
import DandDAPI from "@dndUtilities/system_api";

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

/**
 * These are spells and other abilities.  
 * I wanted to call them Abilities to make them more generic,
 * because I want to use this for things that aren't spells
 * (like Battlemaster Maneuvers, Weapon Attacks, Sneak Attack, etc).
 * 
 * bonus is a string, but it's complicated, so I'm going to 
 * define how it works here.
 * To help me make sure I understand it myself, I'm going to start
 * with some examples:
 * 
 * Weapon(die, bonus): 1 * {die} + {STR} + {bonus}
 * Range(die, bonus): 1 * {die} + {DEX} + {bonus}
 * Finesse(die, bonus): 1 * {die} + Max({STR},{DEX}) + {bonus}
 * SecondWeapon(die, bonus): 1 * {die} + {bonus}
 * SneakAttack(): Ceil({ClassLevel}/2) * {d6} 
 * 
 * EldritchBlast(): {d10} + ({Features[EldritchInvocations]}.includes("Agonizing Blast") ? {CHA} : 0)
 * Hex(): {d6}
 * BurningHands(slotLevel): (2 + {slotLevel}) * {d6}
 * CureWounds(slotLevel): {slotLevel} * {d8} + {SPM}
 * Note: Multiplier on a die roll doesn't mean roll once 
 * and then multiply.  
 * It means roll that many of it and sum.
 * Spells will each need their own formula.
 * And some will need two formulae, like Ice Knife.
 * Also Versatile weapons will pass different die based on 
 * one and two handed strikes.
 * 
 */
export class Spell extends ModelBase {
  data_type: DataType = "spell";
  effects: AbilityEffect[] = []; // Formula for how much damage/healing to do
  concentration = false;
  ritual = false;
  notes = "";
  duration: DurationType | "" = "Instantaneous";
  special_duration = "";
  components: ("V" | "S" | "M")[] = []; // VSM
  material_component = "";
  casting_time: CastingTime = "A"; // A, BA, RA, X minute(s), etc
  special_casting_time = "";
  school: SchoolOfMagic | "" = "";
  level: SpellLevel = 0;

  get level_and_school(): string {
    if (this.level === 0) {
      return `${this.school} Cantrip`;
    } else {
      return `${this.level_string} Level ${this.school}`;
    }
  }
  
  // This flag is to specify whether it triggers a disease effect.  
  // Used by the Apothecary Pathogenist in SCgtD.
  get disease() {
    return this.tags.includes("Disease");
  }
  set disease(value: boolean) {
    if (value) {
      this.tags.push("Disease");
    } else {
      this.tags = this.tags.filter(o => o !== "Disease");
    }
  }
  // This flag is to specify whether it contaminates the user 
  // when they cast the spell.
  get contaminated() {
    return this.tags.includes("Contaminated");
  }
  set contaminated(value: boolean) {
    if (value) {
      this.tags.push("Contaminated");
    } else {
      this.tags = this.tags.filter(o => o !== "Contaminated");
    }
  }
  get charm() {
    return this.tags.includes("Charm");
  }
  set charm(value: boolean) {
    if (value) {
      this.tags.push("Charm");
    } else {
      this.tags = this.tags.filter(o => o !== "Charm");
    }
  }
  get sight() {
    return this.tags.includes("Sight");
  }
  set sight(value: boolean) {
    if (value) {
      this.tags.push("Sight");
    } else {
      this.tags = this.tags.filter(o => o !== "Sight");
    }
  }
  // This flag is used to specify which Spells the 
  // 1D&D UA7 version of Twinned Spell can work with, 
  // which is the same as what is now used in 5.5e
  get twinnable_1dd_ua7() {
    return this.tags.includes("Twinnable");
  }
  set twinnable_1dd_ua7(value: boolean) {
    if (value) {
      this.tags.push("Twinnable");
    } else {
      this.tags = this.tags.filter(o => o !== "Twinnable");
    }
  }
  get chronomancy() {
    return this.tags.includes("Chronomancy");
  }
  set chronomancy(value: boolean) {
    if (value) {
      this.tags.push("Chronomancy");
    } else {
      this.tags = this.tags.filter(o => o !== "Chronomancy");
    }
  }
  get dunamancy() {
    return this.tags.includes("Dunamancy");
  }
  set dunamancy(value: boolean) {
    if (value) {
      this.tags.push("Dunamancy");
    } else {
      this.tags = this.tags.filter(o => o !== "Dunamancy");
    }
  }

  get upcast_string(): string {
    const desc = this.description;
  
    const markers = [
      "At Higher Levels",
      "Using a Higher-Level Spell Slot",
      "Cantrip Upgrade",
      "when you reach 5th",
    ];
  
    for (const marker of markers) {
      const markerIdx = desc.indexOf(marker);
      if (markerIdx === -1) continue;
  
      // Walk back from the marker to find the last line break before it
      const lastBreak = desc.lastIndexOf("\n", markerIdx - 1);
      if (lastBreak === -1) continue; // No line break before marker — skip
  
      return desc.slice(lastBreak + 1).trim();
    }
  
    return "";
  }

  knowledge_usage_key = "";
  knowledge_type: KnowledgeType = "None";
  obj_filter: any = {}; // These are the only ones that are learnable sans KnowledgeFeature.
  spell_list_ids: string[] = []; // Populated on load

  parse_errors: string[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      if (this.description.includes("Cantrip Upgrade.") && !this.description.includes("<b>")) {
        this.description = this.description.replace("Cantrip Upgrade.", "<b>Cantrip Upgrade.</b>");
      } else if (this.description.includes("Using a Higher-Level Spell Slot.") && !this.description.includes("<b>")) {
        this.description = this.description.replace("Using a Higher-Level Spell Slot.", "<b>Using a Higher-Level Spell Slot.</b>");
      } 
      const log_it = false; // this.name === "Burning Hands";
      // if (this.name === "Summon Shadowspawn") {
      //   // log_it = true;
      //   debugger;
      // }
      this.level = obj.level ? obj.level : 0;
      const saving_throw_ability_score = obj.saving_throw_ability_score ? obj.saving_throw_ability_score : "";
      this.effects = []; 
      const range = obj.range ? obj.range : "";
      const range_2 = obj.range_2 ? obj.range_2 : "";
      if (obj.effects && obj.effects.length) {
        for (const e of obj.effects) {
          // if (this.name.includes("Familiar")) {
          //   debugger;
          // }
          const effect = new AbilityEffect(e); // , log_it);
          if (log_it) {
            debugger;
          }
          if (this.level === 0) {
            effect.potence_type = "Character";
          } else {
            effect.potence_type = "Slot";
          }
          if (effect.target_int.range.base === 0 && range !== "") {
            const common_util = CommonUtilities.getInstance();             
            effect.target_int.range.base = common_util.clean_number_string(range);
            // effect.target_int.area.base = common_util.clean_number_string(range_2);
          }
          if (saving_throw_ability_score && !e.saving_throw_ability_score) {
            effect.saving_throw_ability_score = saving_throw_ability_score;
          }
          if (log_it) {
            debugger;
          }
          this.effects.push(effect);
        }
      } else if (obj.effect) {
        const effect = new AbilityEffect(obj.effect);
        if (effect.target_int.range.base === 0 && range !== "") {
          const common_util = CommonUtilities.getInstance();             
          effect.target_int.range.base = common_util.clean_number_string(range);
          effect.target_int.area.base = common_util.clean_number_string(range_2);
        }
        if (saving_throw_ability_score && !obj.effect.saving_throw_ability_score) {
          effect.saving_throw_ability_score = saving_throw_ability_score;
        }
        this.effects.push(effect);
      }
   
      this.concentration = obj.concentration ? obj.concentration : false;
      this.ritual = obj.ritual ? obj.ritual : false;
      this.notes = obj.notes ? obj.notes : "";
      this.duration = obj.duration ? obj.duration : "Instantaneous";
      this.components = obj.components ? [...obj.components] : [];
      this.material_component = obj.material_component ? obj.material_component : "";
      // this.material_component_limit = obj.material_component_limit ? new ItemLimitation(obj.material_component_limit) : new ItemLimitation();
      // this.material_consumed = obj.material_consumed ? obj.material_consumed : this.material_consumed_old;
      // this.min_value = obj.min_value ? obj.min_value : 0;
      this.casting_time = obj.casting_time ? obj.casting_time : "A";
      this.special_casting_time = obj.special_casting_time ? obj.special_casting_time : "";
      if (this.casting_time === "Special" && this.special_casting_time === "") {
        console.log(this.name + " " + obj.casting_time);
      }
      if (!STANDARD_CASTING_TIMES.includes(this.casting_time)) {
        this.special_casting_time = this.casting_time;
        this.casting_time = "Special";
      }
      this.school = obj.school ? obj.school : "";

      if (obj.twinnable_1dd_ua7) {
        this.twinnable_1dd_ua7 = obj.twinnable_1dd_ua7 ? obj.twinnable_1dd_ua7 : false;
        if (this.twinnable_1dd_ua7 && this.effects.length === 1 && this.effects[0].potences.length === 0) {
          const potence = new Potence();
          potence.level = this.level;
          potence.extra = "1 Target";
          this.effects[0].potences.push(potence);
          for (let i = this.level + 1; i <= 9; ++i) {
            const potence = new Potence();
            potence.level = i;
            potence.extra = `${i - this.level + 1} Targets`;
            this.effects[0].potences.push(potence);
          }
        }
      }
      if (obj.disease) {
        this.disease = obj.disease ? obj.disease : false;
        if (obj.disease === undefined) {
          this.disease = this.description.toLocaleLowerCase().includes("disease");
        }
      }
      if (obj.charm) {
        this.charm = obj.charm ? obj.charm : false;
        if (obj.charm === undefined) {
          this.charm = this.description.toLocaleLowerCase().includes("charm");
        } else if (!this.charm && this.description.toLocaleLowerCase().includes("charm")) {
          // console.log(this.name);
        }
      }
      if (obj.sight) {
        this.sight = obj.sight ? obj.sight : false;
      }
      // if (obj.sight === undefined) {
      //   this.sight = this.description.toLocaleLowerCase().includes("see");
      // } else if (!this.sight && this.description.toLocaleLowerCase().includes("can see")) {
      //   console.log(obj);
      // }
      // if (this.name === "Delerium Orb") {
      //   debugger;
      // }
      if (obj.contaminated) {
        this.contaminated = obj.contaminated ? obj.contaminated : false;
        // Contamination Condition _id = 65ac9b24fa583fa0b8d18ec0
        if (obj.contaminated === undefined && this.effect_types.includes("Self Condition")) {
          const contamination = this.effects.filter(o => o.type === "Self Condition" && o.potences.filter(o2 => o2.conditions_applied.includes("65ac9b24fa583fa0b8d18ec0")).length > 0);
          this.contaminated = contamination.length > 0;
        }
      }

      if (this.description.includes("At Higher Levels") && !this.description.includes("<b>At Higher Levels")) {
        if (this.description.includes("At Higher Levels.")) {
          this.description.replace("At Higher Levels.", "<b>At Higher Levels.</b>");
        } else {
          this.description.replace("At Higher Levels", "<b>At Higher Levels.</b>");
        }
      }

      this._area_types = obj._area_types ? obj._area_types : [];
      this._attack_types = obj._attack_types ? obj._attack_types : [];
      this._damage_types = obj._damage_types ? obj._damage_types : [];
      this._effect_types = obj._effect_types ? obj._effect_types : [];
      this._target_types = obj._target_types ? obj._target_types : [];
      this._upcasts = obj._upcasts ? obj._upcasts : false;
    }
  }

  get spell_id() {
    return this._id;
  }

  _effect_types: EffectType[] = [];
  get effect_types(): EffectType[] {
    if (this.loaded) {
      return common_util.unique_array(this.effects.map(o => o.type));
    } else {
      return this._effect_types;
    }
  }

  get effect_types_string() {
    return this.effect_types.join(", ");
  }

  _attack_types: AttackType[] = [];
  get attack_types(): AttackType[] {
    if (this.loaded) {
      return common_util.unique_array(this.effects.map(o => o.attack_type));
    } else {
      return this._attack_types;
    }
  }

  get attack_types_string() {
    return this.attack_types.join(", ");
  }

  _damage_types: DamageType[] = [];
  get damage_types(): DamageType[] {
    if (this.loaded) {
      return common_util.unique_array(this.effects.flatMap(o => o.the_damage_types));
    } else {
      return this._damage_types;
    }
  }

  get damage_types_string() {
    return this.damage_types.join(", ");
  }

  get the_utility_details(): string[] {
    return this.effects.filter(o => ["Control","Utility"].includes(o.type)).map(o => o.utility_detail);
  }

  _target_types: TargetType[] = [];
  get target_types() {
    if (this.loaded) {
      return common_util.unique_array(this.effects.map(o => o.target_int.target_type));
    } else {
      return this._target_types;
    }
  }

  get target_types_string() {
    return this.target_types.join(", ");
  }

  _area_types: AreaType[] = [];
  get area_types() {
    if (this.loaded) {
      return common_util.unique_array(this.effects.filter(o => o.target_int.target_type === "Area").map(o => o.target_int.area_type));
    } else {
      return this._area_types;
    }
  }

  get area_types_string() {
    return this.area_types.join(", ");
  }

  get range_string() {
    const effect = this.effects.find(o => !o.target_int.range.isBlank());
    if (effect) {
      return effect.range_string;
    }
    return "";
  }

  get material_cost_listed(): boolean {
    if (this.components.includes("M")) {
      return this.material_component.toLocaleLowerCase().includes("worth");
    }
    return false;
  }

  get material_consumed(): boolean {
    if (this.components.includes("M")) {
      return this.material_component.toLocaleLowerCase().includes("consume");
    }
    return false;
  }

  get casting_times(): CastingTime[] {
    return [this.casting_time];
  }

  _upcasts = false;
  get upcasts() {
    if (this.loaded) {
      return this.effects.filter(o => o.potences.length > 1).length > 0;
    } else {
      return this._upcasts;
    }
  }

  // get casting_time_string(): string {
  //   switch (this.casting_time) {
  //     case "A": return "Action";
  //     case "BA": return "Bonus Action";
  //     case "RA": return "Reaction";
  //     default: return this.casting_time;
  //   }
  // }

  get range(): string {
    const ranges = this.effects.map(o => o.range_string);
    return ranges.length > 0 ? ranges[0] : "";
  }

  get components_string(): string {
    let components = this.components.join(", ");
    if (this.components.includes("M")) {
      const consumed_string = this.material_consumed && !this.material_component.includes("onsumed") ? " which is consumed by the spell" : "";
      components += ` (${this.material_component}${consumed_string})`;
    }
    return components;
  }

  get level_string_small(): string {
    let str = "";
    switch (this.level) {
      case 0: return "Cantrip";
      case 1: str = "1st"; break;
      case 2: str = "2nd"; break;
      case 3: str = "3rd"; break;
      default: str = `${this.level}th`; break;
    }
    return str;
  }

  get level_string() {
    if (this.level === 0) {
      return "Cantrip";
    }
    return `${this.level_string_small} Level`;
  }

  get attack_save(): string {
    let attack_save = "None";
    const attack_saves = this.effects.map(o => {
      let response: string = o.attack_type;
      if (response === "Save") {
        response = `${o.saving_throw_ability_score} Save`;
      }
      return response;
    });
    if (attack_saves.includes("Melee Spell")) {
      attack_save = "Melee Spell Attack";
    } else if (attack_saves.includes("Ranged Spell")) {
      attack_save = "Ranged Spell Attack";
    }
    // if (attack_saves.includes("Save")) {
    //   if (attack_save === "None") {
    //     attack_save = `${this.saving_throw_ability_score} Save`;
    //   } else {
    //     attack_save += `/${this.saving_throw_ability_score} Save`;
    //   }
    // }
    return attack_save;
  }

  get_detail_format() {
    const str = `${super.get_detail_format()}; ${this.level_string} ${this.school}`;
    return str.trim();
  }

  get_homebrewery_string(): string {
    let str = `# ${this.true_name}\n\r`;
    str += `#### Level: ${this.level}\n\r`;
    if (this.casting_time !== "Special") {
      str += `#### Casting Time: ${CASTING_TIME_MAP[this.casting_time]}\n\r`;
    }
    const range = this.range;
    if (range !== "") {
      str += `#### Range/Area: ${this.range}\n\r`;
    }
    str += `#### Components: ${this.components_string}\n\r`;
    str += `#### Duration: ${this.duration}\n\r`;
    str += `#### School: ${this.school}\n\r`;
    str += `#### Attack/Save: ${this.attack_save}\n\r`;
    str += `#### Damage/Effect: ${this.effect_types.join(", ")}\n\r`;
    const spell_lists = (api.smart_hash.spell_list as SpellList[]).filter(o => this.spell_list_ids.includes(o._id));
    if (spell_lists.length > 0) {
      str += `#### Spell List(s): ${spell_lists.map(o => o.true_name).join(", ")}\n\r`;
    }
    str += `${this.description}\n\r`;
    return str;
  }

  toDBObj(): any {
    const effects: any[] = [];
    for (let i = 0; i < this.effects.length; i++) {
      effects.push(this.effects[i].toDBObj());
    }
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      // saving_throw_ability_score: this.saving_throw_ability_score,
      effects,
      // range: this.range,
      // range_2: this.range_2,
      concentration: this.concentration,
      ritual: this.ritual,
      notes: this.notes,
      duration: this.duration,
      components: this.components,
      material_component: this.material_component,
      // material_component_limit: this.material_component_limit.toDBObj(),
      // material_consumed: this.material_consumed,
      // min_value: this.min_value,
      casting_time: this.casting_time,
      special_casting_time: this.special_casting_time,
      school: this.school,
      level: this.level,
      // knowledge_usage_key: this.knowledge_usage_key,
      // knowledge_type: this.knowledge_type,
      // obj_filter: this.obj_filter,
      // twinnable_1dd_ua7: this.twinnable_1dd_ua7,
      // disease: this.disease,
      // charm: this.charm,
      // contaminated: this.contaminated,
      // sight: this.sight
    };
  }

  toLight(): any {
    return this.toDBObj();
  }

  // toLight(): any {
  //   const base_db_obj = super.toLight();
  //   return {
  //     ...base_db_obj,
  //     concentration: this.concentration,
  //     ritual: this.ritual,
  //     notes: this.notes,
  //     duration: this.duration,
  //     components: this.components,
  //     material_component: this.material_component,
  //     casting_time: this.casting_time,
  //     special_casting_time: this.special_casting_time,
  //     school: this.school,
  //     level: this.level,
  //     twinnable_1dd_ua7: this.twinnable_1dd_ua7,
  //     disease: this.disease,
  //     charm: this.charm,
  //     contaminated: this.contaminated,
  //     sight: this.sight,
  //     _area_types: this.area_types,
  //     _attack_types: this.attack_types,
  //     _damage_types: this.damage_types,
  //     _effect_types: this.effect_types,
  //     _target_types: this.target_types,
  //     _upcasts: this.upcasts
  //   };
  // }

  // get effect(): AbilityEffect {
  //   if (this.effects.length > 0) {
  //     return this.effects[0];
  //   } else {
  //     return new AbilityEffect();
  //   }
  // }

  copy(copyMe: Spell, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    // this.saving_throw_ability_score = copyMe.saving_throw_ability_score;
    // debugger;
    this.effects = copyMe.effects.map(o => o.clone(drop_ids)); // [];
    // this.range = copyMe.range;
    // this.range_2 = copyMe.range_2;
    this.concentration = copyMe.concentration;
    this.ritual = copyMe.ritual;
    this.notes = copyMe.notes;
    this.duration = copyMe.duration;
    this.components = [...copyMe.components];
    this.material_component = copyMe.material_component;
    // this.material_component_limit = copyMe.material_component_limit.clone();
    // this.material_consumed = copyMe.material_consumed;
    // this.min_value = copyMe.min_value;
    this.casting_time = copyMe.casting_time;
    this.special_casting_time = copyMe.special_casting_time;
    this.school = copyMe.school;
    this.level = copyMe.level;
    // this.knowledge_usage_key = copyMe.knowledge_usage_key;
    // this.knowledge_type = copyMe.knowledge_type;
    // this.obj_filter = {...copyMe.obj_filter};
    // this.twinnable_1dd_ua7 = copyMe.twinnable_1dd_ua7;
    // this.disease = copyMe.disease;
    // this.charm = copyMe.charm;
    // this.contaminated = copyMe.contaminated;
    // this.sight = copyMe.sight;
  }

  clone(drop_ids = false): Spell {
    const new_obj = new Spell();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copyTemplate(copyMe: TemplateBase): void {
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.source_books_old = [];
    copyMe.source_books_old.forEach(sb => {
      this.source_books_old.push({...sb});
    });
    const obj = {...copyMe.item};
    const saving_throw_ability_score = obj ? obj.saving_throw_ability_score : null;
    this.effects = []; 
    if (obj && obj.effects && obj.effects.length) {
      obj.effects.forEach((effect: any) => {
        const new_effect = new AbilityEffect(effect);
        new_effect.saving_throw_ability_score = saving_throw_ability_score;
        this.effects.push(new_effect);
      });
    } else if (obj && obj.effect) {
      const new_effect = new AbilityEffect(obj.effect);
      new_effect.saving_throw_ability_score = saving_throw_ability_score;
      this.effects.push(new_effect);
    }
    // this.range = obj ? obj.range : null;
    // this.range_2 = obj ? obj.range_2 : null;
    this.concentration = obj ? obj.concentration : false;
    this.ritual = obj ? obj.ritual : false;
    this.notes = obj ? obj.notes : null;
    this.duration = obj ? obj.duration : "Instantaneous";
    this.components = obj ? [...obj.components] : [];
    this.material_component = obj ? obj.material_component : "";
    // this.material_component_limit = obj && obj.material_component_limit ? new ItemLimitation(obj.material_component_limit) : new ItemLimitation();
    // this.material_consumed = obj && obj.material_consumed ? obj.material_consumed : false;
    // this.min_value = obj && obj.min_value ? obj.min_value : 0;
    this.casting_time = obj ? obj.casting_time : "A";
    this.special_casting_time = obj ? obj.special_casting_time : "";
    this.school = obj ? obj.school : "";
    this.level = obj && obj.level ? obj.level : 0;
    // this.knowledge_usage_key = obj && obj.knowledge_usage_key ? obj.knowledge_usage_key : "";
    // this.knowledge_type = obj && obj.knowledge_type ? obj.knowledge_type : "None";
    // this.obj_filter = obj && obj.obj_filter ? {...obj.obj_filter} : {};
    this.twinnable_1dd_ua7 = obj && obj.twinnable_1dd_ua7 ? obj.twinnable_1dd_ua7 : false;
    this.disease = obj && obj.disease ? obj.disease : false;
    if (obj && obj.disease === undefined) {
      this.disease = this.description.toLocaleLowerCase().includes("disease");
    }
    this.charm = obj && obj.charm ? obj.charm : false;
    if (obj && obj.charm === undefined) {
      this.charm = this.description.toLocaleLowerCase().includes("charm");
    }
    this.contaminated = obj && obj.contaminated ? obj.contaminated : false;
    // Contamination Condition _id = 65ac9b24fa583fa0b8d18ec0
    if (obj && obj.contaminated === undefined && this.effect_types.includes("Self Condition")) {
      const contamination = this.effects.filter(o => o.type === "Self Condition" && o.potences.filter(o2 => o2.conditions_applied.includes("65ac9b24fa583fa0b8d18ec0")).length > 0);
      this.contaminated = contamination.length > 0;
    }
    if (obj && obj.sight === undefined) {
      this.sight = this.description.toLocaleLowerCase().includes("sight") || this.description.toLocaleLowerCase().includes("see");
    }
  }

  copy5e(copyMe: any): void {
    console.log(copyMe);
    // this.name = copyMe.name;
    // this.description = "";
    // copyMe.desc.forEach((d: string) => {
    //   this.description += d + " \n";
    // });
    // this.saving_throw_ability_score = null; // copyMe.saving_throw_ability_score;
    // this.effect = new AbilityEffect();
    // if (copyMe.dc) {
    //   this.saving_throw_ability_score = copyMe.dc.dc_type.name;
    // }
    // let m: any = null;
    // if (copyMe.heal_at_slot_level) {
    //   this.effect.type = "Healing";
    //   this.effect.potence_type = "Slot";
    //   m = copyMe.heal_at_slot_level;
    // } else if (copyMe.damage) {
    //   m = copyMe.damage;
    //   if (m.damage_type && m.damage_type.name) {
    //     this.effect.type = m.damage_type.name;
    //   }
    //   if (m.damage_at_character_level) {
    //     this.effect.potence_type = "Character";
    //     m = m.damage_at_character_level;
    //   } else if (m.damage_at_slot_level) {
    //     this.effect.potence_type = "Slot";
    //     m = m.damage_at_slot_level;
    //   }
    // }
    // this.effect.potences = [];
    // if (m) {
    //   Object.keys(m).forEach((key: string) => {
    //     if (this.effect && m[+key]) {
    //       const bonus_mod = m[+key].split(" + ");
    //       const bonus = bonus_mod[0].split("d");
    //       if (bonus_mod.length > 1) {
    //         this.effect.add_modifier = "true";
    //       }
    //       const dice_count: number = +bonus[0];
    //       const dice_size: number = bonus.length === 2 ? +bonus[1] : 1;
    //       this.effect.potences.push(new AbilityPotence(this.effect.potences.length, +key, dice_count, dice_size));
    //     }
    //   });
    // }
    // this.effect_2 = new AbilityEffect();
    // // this.potence = copyMe.damage?.damage_type?.name;
    // // this.bonus_2 = null; // copyMe.bonus_2;
    // // this.damage_type_2 = null; // copyMe.damage_type_2;
    // this.range = copyMe.range;
    // this.range_2 = null; // copyMe.range_2;
    // this.concentration = copyMe.concentration;
    // this.ritual = copyMe.ritual;
    // this.notes = null; // copyMe.notes;
    // if (copyMe.duration.includes("Up to")) {
    //   copyMe.duration = copyMe.duration.substring(6);
    // }
    // this.duration = copyMe.duration;
    // switch (copyMe.duration) {
    //   case "1 minute":
    //     this.duration = "1 Minute";
    //     break;
    //   case "10 minutes":
    //     this.duration = "10 Minutes";
    //     break;
    //   case "1 hour":
    //     this.duration = "1 Hour";
    //     break;
    //   case "8 hours":
    //     this.duration = "8 Hours";
    //     break;
    //   case "24 hours":
    //     this.duration = "24 Hours";
    //     break;
    //   case "1 round":
    //     this.duration = "1 Round";
    //     break;
    //   case "2 rounds":
    //     this.duration = "2 Rounds";
    //     break;
    //   default:
    //     this.duration = copyMe.duration;
    //     break;
    // }
    // this.components = [...copyMe.components];
    // this.material_component = copyMe.material;
    // switch (copyMe.casting_time) {
    //   case "1 action":
    //     this.casting_time = "A";
    //     break;
    //   case "1 bonus action":
    //     this.casting_time = "BA";
    //     break;
    //   case "1 reaction":
    //     this.casting_time = "RA";
    //     break;
    //   default:
    //     this.casting_time = "Special";
    //     break;
    // }
    // this.school = copyMe.school.name;
    // this.level = copyMe.level;
  }

  connectUserRatings(userRatings: FeatureUserRating[]) {
    this.effects.forEach(fb => {
      fb.connectUserRatings(userRatings);
    });
  }
}